import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { Typography, Box, Alert } from '@mui/material';
import Loading from '../common/Loading';
import BeaconSignInContainer from './BeaconSignIn';
import {
  parseCallbackParams,
  getStoredCodeVerifier,
  exchangeCodeForTokens
} from 'src/utils/cognitoAuth';
import { v2IssueToken, V2LoginResponse, resolveDefaultDivision } from 'src/store/modules/auth/v2Operations';
import { saveDivisions, saveCurrentDivisionId } from 'src/utils/divisionStorage';
import { getAppUrlForReferrer, getReferrerInfo, getAuthFlowValue, clearAuthFlowData } from 'src/utils/ssoReferrer';
import { authOperations } from 'src/store/modules/auth';
import './SignIn.scss';

interface AuthCallbackProps extends RouteComponentProps {
  app: any;
  auth: any;
  setErrorMessage: (msg: string) => void;
}

type CallbackStep = 'processing' | 'error';

/**
 * Resolve referrer with stage suffix, preferring OAuth state over localStorage fallback.
 */
function resolveReferrerFromState(state: Record<string, any> | null): string {
  let referrer = state?.referrer
    || getAuthFlowValue('x-sl-sso-signin-referrer')
    || 'atlas';

  const { stage } = getReferrerInfo();
  if (stage !== 'prod' && !referrer.includes(`-${stage}`)) {
    referrer = `${referrer}-${stage}`;
  }
  return referrer;
}

// Map referrer app names to their valid hostname patterns
const REFERRER_HOSTNAME_PATTERNS: Record<string, string[]> = {
  advisor: ['advisor', 'zenith'],
  zenith: ['advisor', 'zenith'],
};

function resolveReturnUrlFromState(state: Record<string, any> | null, referrer?: string): string | undefined {
  const returnUrl = state?.returnUrl
    || getAuthFlowValue('x-sl-sso-signin-return-url')
    || undefined;

  // Validate that the returnUrl domain matches the referrer to prevent cross-app contamination
  if (returnUrl && referrer) {
    try {
      const returnHost = new URL(returnUrl).hostname.toLowerCase();
      const referrerApp = referrer.replace(/-(?:localhost|dev|alpha|staging)$/, '');
      const validPatterns = REFERRER_HOSTNAME_PATTERNS[referrerApp] || [referrerApp];
      const isValid = validPatterns.some(pattern => returnHost.includes(pattern));
      if (!isValid) {
        console.warn('[AuthCallback] Discarding returnUrl - does not match referrer:', { returnUrl, referrer, validPatterns });
        return undefined;
      }
    } catch {
      console.warn('[AuthCallback] Discarding invalid returnUrl:', returnUrl);
      return undefined;
    }
  }

  return returnUrl;
}

/**
 * After v2IssueToken succeeds, persist division info and redirect to the target app.
 */
async function completeSingleDivisionFlow(
  loginResponse: V2LoginResponse,
  divisionId: string,
  referrer: string,
  returnUrl: string | undefined
): Promise<void> {
  await v2IssueToken(loginResponse.sessionToken, divisionId, referrer);

  saveDivisions(loginResponse.divisions);
  saveCurrentDivisionId(divisionId);
  clearAuthFlowData();

  window.location.href = getAppUrlForReferrer(referrer, returnUrl);
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ app, auth, history, setErrorMessage }) => {
  const [step, setStep] = useState<CallbackStep>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const { code, state, error: oauthError, errorDescription } = parseCallbackParams();

      if (oauthError) {
        throw new Error(errorDescription || oauthError || 'Authentication failed');
      }
      if (!code) {
        throw new Error('No authorization code received');
      }

      const codeVerifier = getStoredCodeVerifier();
      if (!codeVerifier) {
        throw new Error('PKCE code verifier not found. Please try signing in again.');
      }

      const referrer = resolveReferrerFromState(state);
      const returnUrl = resolveReturnUrlFromState(state, referrer);
      const intendedEmail = localStorage.getItem('x-sl-signin-user-email') || undefined;
      console.log('[AuthCallback] Resolved — referrer:', referrer, 'returnUrl:', returnUrl, 'intendedEmail:', intendedEmail);

      const loginResponse: V2LoginResponse = await exchangeCodeForTokens(code, codeVerifier, referrer, intendedEmail);

      const defaultDiv = resolveDefaultDivision(loginResponse.divisions, loginResponse.lastActiveDivisionId);
      await completeSingleDivisionFlow(
        loginResponse,
        defaultDiv.id,
        referrer,
        returnUrl
      );
    } catch (err: any) {
      console.error('Auth callback error:', err);
      setError(err.message || 'Authentication failed');
      setStep('error');
      setErrorMessage(err.message || 'Authentication failed');
    }
  };

  if (step === 'error') {
    return (
      <div style={{ display: 'flex', width: '100%', height: '100%' }}>
        <div style={{ flex: 1, minWidth: '50%', zIndex: 0 }}>
          <BeaconSignInContainer appName={app.name} notificationMSG={auth.message}>
            <Box sx={{ textAlign: 'center', padding: '40px 20px' }}>
              <Alert severity="error" sx={{ marginBottom: 3 }}>
                {error || 'Authentication failed'}
              </Alert>
              <Typography variant="body1" color="text.secondary" sx={{ marginBottom: 2 }}>
                Please try signing in again.
              </Typography>
              <button
                onClick={() => history.push('/sign_in')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Back to Sign In
              </button>
            </Box>
          </BeaconSignInContainer>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      <div style={{ flex: 1, minWidth: '50%', zIndex: 0 }}>
        <BeaconSignInContainer appName={app.name} notificationMSG={auth.message}>
          <Loading className="spinner" size={150} thickness={2} />
        </BeaconSignInContainer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  app: state.app,
  auth: state.auth
});

const mapDispatchToProps = {
  setErrorMessage: authOperations.setErrorMessage
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthCallback));
